/**
 * Immediate-mode widgets for the kit: each frame the panels draw themselves and register the
 * zones the pointer can hit, so drawing and hit-testing can never disagree.
 */

import { makeCanvas } from '../../render/canvas';
import { css } from '../../render/font';
import type { Screen } from '../../render/screen';
import { BLACK, DARK_GREY, GREY, LIGHT_BLUE, LIGHT_GREY, WHITE, YELLOW } from '../../render/palette';
import type { Rect } from './layout';
import { INK, inside } from './layout';

export interface Zone extends Rect {
  readonly id: string;
  /** Shown in the status line while hovered. */
  readonly tip?: string;
  /** button: 0 left, 2 right. */
  readonly click?: (button: number) => void;
  readonly wheel?: (delta: number) => void;
  /** While held, the click repeats (steppers). */
  readonly repeat?: boolean;
}

export class Ui {
  screen!: Screen;
  /** The 25 Hz animation clock. */
  anim = 0;
  /** The pointer in screen pixels; -1 before it has moved. */
  px = -1;
  py = -1;
  private zones: Zone[] = [];
  private hoverId: string | null = null;
  /** The zone under the pointer when this frame began. */
  hovered: Zone | undefined;

  /** Start a frame: hover is judged against last frame's zones, then they are forgotten. */
  begin(screen: Screen, anim: number): void {
    this.screen = screen;
    this.anim = anim;
    this.hovered = this.at(this.px, this.py);
    this.hoverId = this.hovered?.id ?? null;
    this.zones = [];
  }

  add(z: Zone): void {
    this.zones.push(z);
  }

  /** A dialog opens: nothing drawn before it can be hit. */
  block(): void {
    this.zones = [];
  }

  /** The topmost zone at a point, from the frame drawn most recently. */
  at(x: number, y: number): Zone | undefined {
    for (let i = this.zones.length - 1; i >= 0; i--) {
      const z = this.zones[i];
      if (z && inside(z, x, y)) return z;
    }
    return undefined;
  }

  hot(id: string): boolean {
    return this.hoverId === id;
  }
}

const patterns = new Map<string, HTMLCanvasElement>();

/** Every other pixel in `colour`: the C64's way to shade without a new colour. */
export function dither(s: Screen, r: Rect, colour: number): void {
  const key = String(colour);
  let tile = patterns.get(key);
  if (!tile) {
    tile = makeCanvas(2, 2);
    const c = tile.getContext('2d');
    if (c) {
      c.fillStyle = css(colour);
      c.fillRect(0, 0, 1, 1);
      c.fillRect(1, 1, 1, 1);
    }
    patterns.set(key, tile);
  }
  const pattern = s.ctx.createPattern(tile, 'repeat');
  if (!pattern) return;
  s.ctx.fillStyle = pattern;
  s.ctx.fillRect(r.x, r.y, r.w, r.h);
}

/** A panel: a one-pixel frame with a title strip. */
export function panel(ui: Ui, r: Rect, title: string, right = ''): void {
  const s = ui.screen;
  s.fill(r.x, r.y, r.w, r.h, BLACK);
  s.box(r.x, r.y, r.w, r.h, INK.frame);
  s.fill(r.x, r.y, r.w, 10, INK.panel);
  s.text(title, r.x + 4, r.y + 1, INK.panelText);
  if (right) s.text(right, r.x + r.w - 4, r.y + 1, INK.panelText, 'right');
}

export interface ButtonOptions {
  readonly id: string;
  readonly tip: string;
  readonly click: (button: number) => void;
  /** Selected (a tool, a toggle that is on). */
  readonly on?: boolean;
  readonly disabled?: boolean;
  /** Label colour when enabled (TEST is green). */
  readonly ink?: number;
}

export function button(ui: Ui, r: Rect, label: string, o: ButtonOptions): void {
  const s = ui.screen;
  const hot = ui.hot(o.id) && !o.disabled;
  s.fill(r.x, r.y, r.w, r.h, o.on ? INK.panel : INK.button);
  s.box(r.x, r.y, r.w, r.h, hot ? YELLOW : o.on ? LIGHT_BLUE : GREY);
  const ink = o.disabled ? GREY : hot ? YELLOW : o.on ? WHITE : (o.ink ?? LIGHT_GREY);
  s.text(label, r.x + r.w / 2, r.y + Math.floor((r.h - 8) / 2), ink, 'center');
  ui.add({ ...r, id: o.id, tip: o.tip, click: o.disabled ? () => {} : o.click });
}

/** A small arrow that repeats while held. */
export function arrow(
  ui: Ui,
  x: number,
  y: number,
  dir: -1 | 1,
  id: string,
  tip: string,
  click: () => void,
  wheel?: (d: number) => void,
): void {
  const hot = ui.hot(id);
  ui.screen.text(dir < 0 ? '◄' : '►', x, y, hot ? YELLOW : LIGHT_BLUE);
  ui.add({ x: x - 1, y: y - 2, w: 10, h: 12, id, tip, click, repeat: true, ...(wheel ? { wheel } : {}) });
}

export interface StepperOptions {
  readonly id: string;
  readonly tip: string;
  readonly focused: boolean;
  readonly change: (delta: number) => void;
  /** Clicking the value itself. */
  readonly press?: () => void;
  readonly valueInk?: number;
}

/** LABEL ◄ value ►, 112 px wide. Wheel over it steps too. */
export function stepper(ui: Ui, x: number, y: number, label: string, value: string, o: StepperOptions): void {
  const s = ui.screen;
  const wheel = (d: number): void => o.change(-d);
  if (o.focused) s.fill(x - 2, y - 2, 116, 12, DARK_GREY);
  s.text(label, x, y, o.focused ? YELLOW : INK.text);
  arrow(ui, x + 52, y, -1, `${o.id}-`, o.tip, () => o.change(-1), wheel);
  s.text(value, x + 101, y, o.valueInk ?? (ui.hot(`${o.id}=`) && o.press ? YELLOW : WHITE), 'right');
  arrow(ui, x + 104, y, 1, `${o.id}+`, o.tip, () => o.change(1), wheel);
  ui.add({ x, y: y - 2, w: 51, h: 12, id: `${o.id}:`, tip: o.tip, wheel });
  const box = { x: x + 61, y: y - 2, w: 42, h: 12, id: `${o.id}=`, tip: o.tip, wheel };
  ui.add(o.press ? { ...box, click: o.press } : box);
}

/** Text cut to `max` characters, ending in '.' when cut. */
export function fit(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, Math.max(0, max - 1))}.`;
}

/** A bordered dialog in the middle of the screen, over a dithered backdrop. Returns its rect. */
export function dialog(ui: Ui, w: number, h: number, title: string): Rect {
  const s = ui.screen;
  dither(s, { x: 0, y: 0, w: s.width, h: s.height }, BLACK);
  ui.block();
  const r = { x: Math.floor((s.width - w) / 2), y: Math.floor((s.height - h) / 2), w, h };
  s.fill(r.x + 3, r.y + 3, w, h, BLACK);
  panel(ui, r, title);
  s.box(r.x - 1, r.y - 1, w + 2, h + 2, BLACK);
  // Clicks on the dialog's empty parts must not fall through to what is underneath.
  ui.add({ ...r, id: 'dialog' });
  return r;
}
